'use client';

import React, { useId, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { addSubscriber, type SubscriberSource } from '@/lib/subscribers';
import { reportConversion } from '@/lib/conversions';

/**
 * One email capture form, used by the assessment and by the article newsletter
 * block, so both write the same shape into the same table and the admin list
 * stays sortable by source.
 *
 * A duplicate is treated as success: someone who submits twice is on the list,
 * and telling them "you are already subscribed" only invites them to wonder
 * whether the first one worked.
 */

export default function EmailCaptureForm({
  source,
  context = '',
  details,
  buttonLabel = 'Subscribe',
  successMessage = 'You are on the list. Thank you.',
  placeholder = 'you@company.com',
  onSuccess,
  dark = false,
}: {
  source: SubscriberSource;
  context?: string;
  details?: Record<string, unknown>;
  buttonLabel?: string;
  successMessage?: string;
  placeholder?: string;
  onSuccess?: (email: string) => void;
  dark?: boolean;
}) {
  const inputId = useId();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const ink = dark ? 'var(--m-on-ink)' : 'var(--m-ink)';
  const border = dark ? 'rgba(var(--m-on-ink-rgb),0.25)' : 'rgba(var(--m-ink-rgb),0.2)';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (state === 'saving') return;
    setState('saving');
    setMessage('');

    // Belt and braces: whatever happens below, the form must not be left
    // sitting on "Saving…" with no explanation.
    try {
      const result = await addSubscriber({ email, source, context, details });
      if (result.ok) {
        setState('done');
        /* Two different conversions share this one form. An address given at
           the end of the assessment is a finished assessment; the same form in
           an article or the footer is a subscription, and Meta counts those as
           different things. Reported before onSuccess, which on the assessment
           navigates away.

           Completing the assessment and then skipping the address is
           deliberately not reported: nothing registered, so calling it a
           registration would inflate the only number the advertising is judged
           on. */
        reportConversion(source === 'assessment' ? 'assessment' : 'subscribe');
        onSuccess?.(email.trim().toLowerCase());
        return;
      }
      setState('error');
      setMessage(result.message);
    } catch (error) {
      console.error('Subscribe failed:', error);
      setState('error');
      setMessage('Something went wrong. Please try again, or email info@noventilabs.com.');
    }
  };

  if (state === 'done') {
    return (
      <p
        className="flex items-center gap-2"
        style={{ color: ink, fontSize: 15 }}
        role="status"
        aria-live="polite"
      >
        <Check className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        {successMessage}
      </p>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="flex flex-col sm:flex-row gap-2">
        <label htmlFor={inputId} className="sr-only">
          Email address
        </label>
        <input
          id={inputId}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          aria-invalid={state === 'error'}
          aria-describedby={state === 'error' ? `${inputId}-error` : undefined}
          style={{
            flex: 1,
            minHeight: 48,
            padding: '12px 14px',
            // 16px keeps iOS Safari from zooming the page on focus.
            fontSize: 16,
            color: ink,
            background: 'transparent',
            border: `1px solid ${border}`,
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={state === 'saving'}
          className="font-mono uppercase tracking-widest inline-flex items-center justify-center gap-2"
          style={{
            minHeight: 48,
            padding: '12px 24px',
            fontSize: 12,
            letterSpacing: '0.12em',
            background: ink,
            color: dark ? 'var(--m-ink)' : 'var(--m-on-ink)',
            border: 'none',
            cursor: state === 'saving' ? 'wait' : 'pointer',
            opacity: state === 'saving' ? 0.6 : 1,
          }}
        >
          {state === 'saving' ? 'Saving…' : buttonLabel}
          {state !== 'saving' && <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />}
        </button>
      </div>
      {state === 'error' && (
        <p
          id={`${inputId}-error`}
          role="alert"
          style={{ marginTop: 8, fontSize: 13, color: '#c00' }}
        >
          {message}
        </p>
      )}
    </form>
  );
}
